// SB6-ANA-POP-02 — move a widget between the main dashboard and pop-out
// windows (Christian's "move to window" + max-3 logic).
//
// Destinations:
//   - "main"            → bring the widget back to the main dashboard.
//   - { windowId }      → move it into an existing pop-out window.
//   - "new"             → open a NEW pop-out window seeded with the widget,
//                         placed maximised-windowed on a second monitor
//                         (reuses Phase-1 `useScreenPlacement`).
//
// Opening a real browser window happens here (a user gesture), synchronously
// in the click handler so the popup blocker is satisfied; second-monitor
// placement is applied afterwards. The store mutation that creates the window
// record is rolled back if the popup is blocked.
import { useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  usePopoutWorkspaceStore,
  MAX_POPOUT_WINDOWS,
} from "./popoutWorkspaceStore.js";
import { useScreenPlacement, type ScreenInfo } from "./useScreenPlacement.js";
import {
  shouldShowBrowserAdvice,
  markBrowserAdviceShown,
} from "./browserAdvice.js";
import { MIN_POPOUT_VIEWPORT_PX, type PopoutWidgetKey } from "./windowSizes.js";

/** Default size for a (multi-widget) pop-out window before placement. */
export const POPOUT_WINDOW_DEFAULT_SIZE = { width: 1280, height: 900 } as const;

export type MoveDestination =
  | { readonly kind: "main" }
  | { readonly kind: "window"; readonly windowId: string }
  | { readonly kind: "new" };

export interface MoveResult {
  readonly ok: boolean;
  /** Popup blocked when opening a new window. */
  readonly blocked?: true;
  /** New-window action skipped because the viewport is < 1024 px. */
  readonly viewportTooSmall?: true;
  /** At the 3-window cap (shouldn't happen — the chooser hides "new"). */
  readonly atMax?: true;
  /** Show the once-per-session "use Chrome/Edge" advice (non-Chromium). */
  readonly advise?: true;
}

export interface UsePopoutWidgetMoveResult {
  readonly move: (widget: PopoutWidgetKey, dest: MoveDestination) => MoveResult;
}

export function usePopoutWidgetMove(): UsePopoutWidgetMoveResult {
  const { inspectionId = "" } = useParams<{ inspectionId: string }>();
  const placement = useScreenPlacement();

  const move = useCallback(
    (widget: PopoutWidgetKey, dest: MoveDestination): MoveResult => {
      const store = usePopoutWorkspaceStore.getState();

      if (dest.kind === "main") {
        store.unassign(widget);
        return { ok: true };
      }
      if (dest.kind === "window") {
        store.assignToWindow(widget, dest.windowId);
        return { ok: true };
      }

      // dest.kind === "new"
      if (typeof window === "undefined") return { ok: false };
      if (window.innerWidth < MIN_POPOUT_VIEWPORT_PX) {
        return { ok: false, viewportTooSmall: true };
      }
      if (store.windows.length >= MAX_POPOUT_WINDOWS) {
        return { ok: false, atMax: true };
      }

      // Spread successive windows across available screens.
      const windowIndex = store.windows.length;
      const windowId = store.openWindowWith(widget);
      if (!windowId) return { ok: false, atMax: true };

      const dims = POPOUT_WINDOW_DEFAULT_SIZE;
      const search = new URLSearchParams({
        popout: "1",
        windowId,
      }).toString();
      const url = "/analysis/" + inspectionId + "?" + search;
      const features = [
        "width=" + String(dims.width),
        "height=" + String(dims.height),
        "menubar=no",
        "toolbar=no",
        "location=no",
        "status=no",
        "resizable=yes",
        "scrollbars=yes",
      ].join(",");
      const child = window.open(url, "aero-lyse-popout-" + windowId, features);
      if (!child) {
        // Roll back the store window we optimistically created.
        store.closeWindow(windowId);
        return { ok: false, blocked: true };
      }

      let advise = false;
      if (placement.supported && placement.extended) {
        void placement.resolveTarget().then((target) => {
          let screen: ScreenInfo | null = null;
          if (target.kind === "auto") screen = target.screen;
          else if (target.kind === "picker") {
            // No dialog in Phase 3 — spread windows across the candidates.
            screen =
              target.screens[windowIndex % target.screens.length] ?? null;
          }
          if (screen) placement.applyPlacement(child, screen);
        });
      } else if (!placement.supported) {
        if (shouldShowBrowserAdvice({ supported: false })) {
          markBrowserAdviceShown();
          advise = true;
        }
      }

      return advise ? { ok: true, advise: true } : { ok: true };
    },
    [inspectionId, placement],
  );

  return { move };
}
